import { olderGeneration, youngerGeneration } from '@/assets/messages';
import { PrimaryAnnotatedType, SecondaryAnnotatedType } from '@/components/annotated-type';
import { DotMatrix } from '@/components/dot-matrix';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { select, t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useState } from 'react';

type OnlineRisksChapter1Props = {
  data: OnlineRisksData;
};

export function OnlineRisksChapter1(props: OnlineRisksChapter1Props) {
  const { data } = props;

  const { i18n } = useLingui();
  const formatPercentage = useNumberFormatting('percent');

  const [dotMatrixData, setDotMatrixData] = useState<AgeGroupBreakdown>();
  const [dotMatrixLabel, setDotMatrixLabel] = useState('');

  const handleFirstStepEnter = (
    evt: OnStepEnterExitArgs<'very_concerned' | 'somewhat_concerned'>
  ) => {
    if (evt.data) {
      setDotMatrixData(data.concerned_with_sharing_personal_info[evt.data]);
      setDotMatrixLabel(
        t({
          id: 'online-risks.chapter-1.chart-data-label',
          message: select(evt.data, {
            very_concerned: '% who are very concerned with sharing personal information',
            somewhat_concerned: '% who are somewhat concerned with sharing personal information',
            not_concerned: '% who are not concerned with sharing personal information',
          }),
        })
      );
    }
  };

  return (
    <>
      <ScrollRoot>
        <ScrollGraphic>
          <DotMatrix
            formatValue={formatPercentage}
            data={dotMatrixData}
            label={dotMatrixLabel}
            getLabel={(side) =>
              ({ left: youngerGeneration(i18n), right: olderGeneration(i18n) }[side])
            }
            getLeft={(d) => d.younger}
            getRight={(d) => d.older}
            getNumberOfCircles={(d) => Math.round(d * 100)}
          />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleFirstStepEnter}>
          <ScrollStep data={'somewhat_concerned'}>
            <StoryText>
              <Trans id="online-risks.chapter-1.text-1">
                On average, the most popular answer among{' '}
                <PrimaryAnnotatedType>young internet users</PrimaryAnnotatedType> is "somewhat
                concerned".
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={'very_concerned'}>
            <StoryText>
              <Trans id="online-risks.chapter-1.text-2">
                But among <SecondaryAnnotatedType>older internet users</SecondaryAnnotatedType>, the
                most popular answer is "very concerned".
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={'not_concerned'}>
            <StoryText>
              <Trans id="online-risks.chapter-1.text-3">
                Equal shares of <PrimaryAnnotatedType>young</PrimaryAnnotatedType> and{' '}
                <SecondaryAnnotatedType>older</SecondaryAnnotatedType> generations reported "not
                being concerned at all".
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>
    </>
  );
}
